import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../providers/SessionProvider";

import { Copy, Shield, ShieldCheck, Smartphone, Trash2, Key, Monitor, MapPin, Clock, LogOut, ChevronDown, ChevronUp, Loader2, HelpCircle, Eye, EyeOff } from "lucide-react";
import { authApi } from "../../api/auth.api";
import { toast } from "../../hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../components/ui/input-otp";

export const SecuritySettings = ({ section = 'all' }) => {
    const { t } = useTranslation('settings');
    // const [is2FAEnabled, setIs2FAEnabled] = useState(false); // Removed local state
    const [isLoading, setIsLoading] = useState(false);

    // 2FA Setup State
    const [isSettingUp2FA, setIsSettingUp2FA] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [setupToken, setSetupToken] = useState("");
    const [tempSecret, setTempSecret] = useState("");
    const [backupCodes, setBackupCodes] = useState([]);
    const [showBackupCodes, setShowBackupCodes] = useState(false);

    const [isEnabling, setIsEnabling] = useState(false);

    // Device List Expansion State
    const [isDevicesExpanded, setIsDevicesExpanded] = useState(false);

    // Deactivation Modal State
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateToken, setDeactivateToken] = useState("");
    const [deactivateError, setDeactivateError] = useState("");

    // Security Questions State (from Context)
    const { userData, refreshUserData } = useSession();
    const securityStatus = userData.securityStatus;
    const sessions = userData.sessions || []; // Use cached sessions
    const is2FAEnabled = userData.twoFactorEnabled; // Use cached 2FA status

    // START: Restore Local State
    const [isSecurityExpanded, setIsSecurityExpanded] = useState(false);
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [securityForm, setSecurityForm] = useState([
        { questionId: "", answer: "" },
        { questionId: "", answer: "" },
        { questionId: "", answer: "" }
    ]);
    const [isSavingSecurity, setIsSavingSecurity] = useState(false);
    const [showAnswers, setShowAnswers] = useState([false, false, false]);
    // END: Restore Local State

    // Sync loading state with context
    useEffect(() => {
        setIsLoading(userData.isLoading);
    }, [userData.isLoading]);

    /* REMOVED LOCAL FETCHING - Handled by SessionProvider */
    /*
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        // ... removed ...
    };
    */


    const handleEnable2FA = async () => {
        setIsEnabling(true);
        try {
            const result = await authApi.generate2FASecret();
            setQrCode(result.qrCodeDataUrl);
            setTempSecret(result.secret);
            setIsSettingUp2FA(true);
        } catch (error) {
            toast({ title: "Erè", description: "M pa ka kreye kòd QR la.", variant: "error" });
        } finally {
            setIsEnabling(false);
        }
    };

    const handleConfirm2FA = async () => {
        setIsEnabling(true);
        try {
            const result = await authApi.enable2FA(setupToken);
            setBackupCodes(result.backupCodes);
            // Update global cache instead of local state
            await refreshUserData();
            // setIs2FAEnabled(true);
            setIsSettingUp2FA(false);
            setShowBackupCodes(true);
            setSetupToken("");
            toast({ title: "Siksè", description: "2FA aktive kounye a!", variant: "success" });
        } catch (error) {
            console.error("Enable 2FA Error:", error);

            // Rescue Logic: Check if it was actually enabled despite the error (e.g. timeout)
            try {
                const status = await authApi.get2FAStatus();
                if (status.twoFactorEnabled) {
                    // It is enabled! Try to rescue by regenerating codes
                    try {
                        const rescueResult = await authApi.regenerateBackupCodes(setupToken);
                        setBackupCodes(rescueResult.backupCodes);
                        await refreshUserData();
                        // setIs2FAEnabled(true);
                        setIsSettingUp2FA(false);
                        setShowBackupCodes(true);
                        setSetupToken("");
                        toast({ title: "Siksè", description: "2FA aktive (rekipere)!", variant: "warning" });
                        return; // Exit successfully
                    } catch (rescueError) {
                        console.error("Rescue failed", rescueError);
                    }
                }
            } catch (statusError) {
                // Ignore status check error
            }

            toast({ title: "Kòd envalid", description: "Tanpri verifye kòd la epi eseye ankò.", variant: "error" });
        } finally {
            setIsEnabling(false);
        }
    };

    const handleCopyBackupCodes = () => {
        const codesString = backupCodes.join("\n");
        navigator.clipboard.writeText(codesString);
        toast({ title: "Kopye", description: "Kòd yo kopye nan pres-papye a.", variant: "success" });
    };

    const handleDownloadBackupCodes = () => {
        const codesString = backupCodes.join("\n");
        const blob = new Blob([`BOUSANM 2FA BACKUP CODES\nDATE: ${new Date().toLocaleString()}\n\n${codesString}`], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "bousanm-backup-codes.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDisable2FA = async () => {
        setDeactivateError("");
        setShowDeactivateModal(true);
    };

    const confirmDisable2FA = async (token = deactivateToken) => {
        setIsEnabling(true);
        setDeactivateError("");
        try {
            await authApi.disable2FA(token);
            await refreshUserData();
            // setIs2FAEnabled(false);
            setShowDeactivateModal(false);
            setDeactivateToken("");
            toast({ title: "Dezaktive", description: "2FA retire sou kont ou.", variant: "warning" });
        } catch (error) {
            console.error(error);
            setDeactivateToken(""); // Clear token on error to allow retry easily? Or keep it? User said "lem ap efase", so maybe keep it.
            // Actually, usually you keep it so they can see what they typed, but for OTP often you clear. 
            // Let's keep it but focusing on setting the error.
            setDeactivateError("Kòd la pa kòrèk. Tanpri verifye l.");
            // toast({ title: "Erè", description: "Kòd la pa bon.", variant: "error" }); // Optional: remove toast if inline is enough
        } finally {
            setIsEnabling(false);
        }
    };

    const handleRevokeSession = async (sessionId) => {
        try {
            await authApi.revokeSession(sessionId);
            await refreshUserData(); // Refresh sessions
            // setSessions(sessions.filter(s => s.id !== sessionId));
            toast({ title: "Siksè", description: "Aparèy la dekonekte.", variant: "success" });
        } catch (error) {
            toast({ title: "Erè", description: "Echèk dekoneksyon.", variant: "error" });
        }
    };

    // ==========================================
    // SECURITY QUESTIONS LOGIC
    // ==========================================

    const loadSecurityQuestions = async () => {
        try {
            const questions = await authApi.getSecurityQuestions();
            setAvailableQuestions(questions);

            // If editing existing, maybe pre-fill? No, for security we usually require re-entry or just show empty.
            if (securityStatus?.hasSecurityQuestions) {
                try {
                    const userQuestions = await authApi.getUserQuestions();
                    // Map to form but leave answers empty
                    const prefilled = [0, 1, 2].map(i => ({
                        questionId: userQuestions.questions[i]?.id || "",
                        answer: ""
                    }));
                    setSecurityForm(prefilled);
                } catch (e) {
                    console.error("Could not fetch user questions", e);
                }
            }
        } catch (error) {
            toast({ title: "Erè", description: "Enposib pou chaje kesyon yo.", variant: "error" });
        }
    };

    const handleSecurityExpand = () => {
        if (!isSecurityExpanded) {
            loadSecurityQuestions();
        }
        setIsSecurityExpanded(!isSecurityExpanded);
    };

    const handleQuestionChange = (index, questionId) => {
        const newForm = [...securityForm];
        newForm[index].questionId = questionId;
        setSecurityForm(newForm);
    };

    const handleAnswerChange = (index, answer) => {
        const newForm = [...securityForm];
        newForm[index].answer = answer;
        setSecurityForm(newForm);
    };

    const toggleAnswerVisibility = (index) => {
        const newShowAnswers = [...showAnswers];
        newShowAnswers[index] = !newShowAnswers[index];
        setShowAnswers(newShowAnswers);
    };
    const handleSaveSecurityQuestions = async () => {
        // Validation
        const uniqueQuestions = new Set(securityForm.map(f => f.questionId));
        if (uniqueQuestions.size !== 3) {
            toast({ title: "Atansyon", description: "Tanpri chwazi 3 kesyon diferan.", variant: "warning" });
            return;
        }
        if (securityForm.some(f => !f.questionId || !f.answer.trim())) {
            toast({ title: "Atansyon", description: "Tout kesyon ak repons yo obligatwa.", variant: "warning" });
            return;
        }

        setIsSavingSecurity(true);
        try {
            await authApi.setupSecurityQuestions(securityForm);
            // setSecurityStatus({ ...securityStatus, hasSecurityQuestions: true }); // Update local
            await refreshUserData(); // Update global cache
            setIsSecurityExpanded(false);
            toast({ title: "Siksè", description: "Kesyon sekirite yo anrejistre!", variant: "success" });
        } catch (error) {
            console.error(error);
            toast({ title: "Erè", description: "Echèk anrejistreman.", variant: "error" });
        } finally {
            setIsSavingSecurity(false);
        }
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500">{t('2fa.loading')}</div>;

    // Helper to parse User Agent
    const parseUserAgent = (uaString) => {
        if (!uaString) return { browser: "Inconnu", os: "Inconnu", type: "unknown" };

        let browser = "Navigatè";
        let os = "Sistèm";
        let type = "unknown";

        // Detect OS & Type
        if (uaString.includes("Mac OS")) {
            os = "macOS";
            type = "desktop";
        } else if (uaString.includes("Windows")) {
            os = "Windows";
            type = "desktop";
        } else if (uaString.includes("Android")) {
            os = "Android";
            type = "mobile";
        } else if (uaString.includes("iPhone") || uaString.includes("iPad")) {
            os = "iOS";
            type = uaString.includes("iPad") ? "tablet" : "mobile";
        } else if (uaString.includes("Linux")) {
            os = "Linux";
            type = "desktop";
        }

        // Detect Browser
        if (uaString.includes("Brave")) browser = "Brave";
        else if (uaString.includes("Chrome") && !uaString.includes("Edg")) browser = "Chrome";
        else if (uaString.includes("Safari") && !uaString.includes("Chrome")) browser = "Safari";
        else if (uaString.includes("Firefox")) browser = "Firefox";
        else if (uaString.includes("Edg")) browser = "Edge";
        else if (uaString.includes("BousanmApp")) browser = "App Bousanm";

        return { browser, os, type };
    };

    const getDeviceIcon = (type) => {
        switch (type) {
            case "mobile": return <Smartphone size={20} />;
            case "tablet": return <Smartphone size={20} className="rotate-90" />; // Fallback if Tablet icon not available
            case "desktop": return <Monitor size={20} />;
            default: return <Monitor size={20} />; // Monitor is usually imported as Laptop or Monitor
        }
    };

    // Helper to format date
    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleString('ht-HT', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 2FA SECTION */}
                <div className={`bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-sm flex flex-col ${is2FAEnabled ? 'h-full' : ''}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                        <div className="flex gap-4">
                            <div className={`p-3 rounded-full shrink-0 h-10 w-10 flex items-center justify-center ${is2FAEnabled ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"}`}>
                                {is2FAEnabled ? <ShieldCheck size={20} /> : <Shield size={20} />}
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 leading-tight">{t('2fa.title')}</h3>
                                <p className="text-gray-500 text-xs mt-1 max-w-xs">
                                    {t('2fa.subtitle')}
                                </p>
                            </div>
                        </div>
                        <div className="self-end sm:self-start shrink-0">
                            {is2FAEnabled ? (
                                <button
                                    onClick={handleDisable2FA}
                                    className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors whitespace-nowrap"
                                >
                                    {t('2fa.disable')}
                                </button>
                            ) : (
                                <button
                                    onClick={handleEnable2FA}
                                    disabled={isEnabling}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm shadow-blue-200"
                                >
                                    {isEnabling && <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />}
                                    <span>{t('2fa.enable')}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content Fills Remaining Space if Enabled */}
                    {isSettingUp2FA && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-2 p-4 bg-white/80 rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                        >
                            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] text-center font-bold">1</span>
                                {t('2fa.setup')}
                            </h4>
                            <div className="flex flex-col gap-4 items-center">
                                <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-100">
                                    {qrCode ? <img src={qrCode} alt="QR Code" className="w-32 h-32 mix-blend-multiply" /> : "Loading..."}
                                </div>

                                <div className="w-full space-y-3">
                                    <div className="text-center sm:text-left">
                                        <p className="text-xs text-gray-600 mb-2">
                                            {t('2fa.scanQr')}
                                        </p>
                                        {tempSecret && (
                                            <div
                                                onClick={() => {
                                                    navigator.clipboard.writeText(tempSecret);
                                                    toast({ title: "Kopye", description: "Kod kopye!" });
                                                }}
                                                className="flex items-center justify-between bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors w-full"
                                            >
                                                <code className="text-xs font-mono font-semibold text-gray-800 break-all">{tempSecret}</code>
                                                <Copy size={12} className="text-gray-400 shrink-0 ml-2" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="border-t border-gray-100 pt-3">
                                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-[10px] text-center font-bold">2</span>
                                            {t('2fa.confirm')}
                                        </h4>
                                        <div className="flex flex-col gap-2">
                                            <input
                                                type="text"
                                                value={setupToken}
                                                onChange={(e) => setSetupToken(e.target.value)}
                                                placeholder="000 000"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 outline-none font-mono text-center tracking-widest text-lg bg-gray-50 focus:bg-white transition-colors"
                                                maxLength={6}
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setIsSettingUp2FA(false)}
                                                    className="flex-1 px-3 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg text-xs transition-colors"
                                                >
                                                    {t('2fa.cancel')}
                                                </button>
                                                <button
                                                    onClick={handleConfirm2FA}
                                                    disabled={setupToken.length < 6 || isEnabling}
                                                    className="flex-1 px-3 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1 text-xs shadow-sm shadow-blue-200"
                                                >
                                                    {isEnabling && <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />}
                                                    <span>{t('2fa.confirm')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {showBackupCodes && (
                        <div className="mt-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200/60 shadow-sm flex-1">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-2">
                                    <div className="p-1 bg-yellow-100 rounded text-yellow-700">
                                        <Key size={14} />
                                    </div>
                                    <h4 className="font-bold text-yellow-900 text-xs">{t('2fa.backupCodes')}</h4>
                                </div>
                                <div className="flex gap-1 bg-white/50 p-0.5 rounded border border-yellow-100">
                                    <button
                                        onClick={handleCopyBackupCodes}
                                        className="p-1 px-2 text-yellow-700 hover:bg-yellow-100 rounded text-[10px] font-bold uppercase"
                                    >
                                        {t('2fa.copy')}
                                    </button>
                                    <div className="w-px bg-yellow-200 my-1"></div>
                                    <button
                                        onClick={handleDownloadBackupCodes}
                                        className="p-1 px-2 text-yellow-700 hover:bg-yellow-100 rounded text-[10px] font-bold uppercase"
                                    >
                                        {t('2fa.download')}
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 font-mono text-[10px] bg-white p-2 rounded-lg border border-yellow-200 text-center mb-2">
                                {backupCodes.map((code, i) => (
                                    <div key={i} className="text-gray-600 select-all">{code}</div>
                                ))}
                            </div>
                            <button
                                onClick={() => setShowBackupCodes(false)}
                                className="text-xs font-semibold text-yellow-800 hover:text-yellow-900 w-full text-center"
                            >
                                {t('2fa.close')}
                            </button>
                        </div>
                    )}

                    {/* Visual filler for equal height when not active */}
                    {!isSettingUp2FA && !showBackupCodes && is2FAEnabled && (
                        <div className="flex-1 min-h-[100px] flex items-center justify-center border-t border-dashed border-gray-100 mt-4 pt-4">
                            <div className="text-center">
                                <ShieldCheck className="mx-auto text-green-100 mb-2" size={48} />
                                <p className="text-xs text-green-600 font-medium">{t('2fa.protected')}</p>
                            </div>
                        </div>
                    )}
                    {!isSettingUp2FA && !showBackupCodes && !is2FAEnabled && (
                        <div className="flex-1 min-h-[100px] flex items-center justify-center border-t border-dashed border-gray-100 mt-4 pt-4">
                            <div className="text-center">
                                <Shield className="mx-auto text-gray-100 mb-2" size={48} />
                                <p className="text-xs text-gray-400">{t('2fa.notProtected')}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* SESSIONS SECTION */}
                <div className={`bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-sm flex flex-col h-full`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-full shrink-0 h-10 w-10 flex items-center justify-center">
                                <Smartphone size={20} />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">{t('sessions.title')}</h3>
                                <p className="text-gray-500 text-xs">{t('sessions.subtitle')}</p>
                            </div>
                        </div>
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-bold">
                            {sessions.length}
                        </span>
                    </div>

                    <div className="space-y-3 flex-1">
                        {/* Logic for collapsing sessions */}
                        {(isDevicesExpanded ? sessions : sessions.slice(0, 2)).map((session) => {
                            const { browser, os, type } = parseUserAgent(session.userAgent);

                            return (
                                <div key={session.id} className="flex items-center justify-between p-3 rounded-xl bg-white border border-gray-100 shadow-sm hover:border-blue-100 transition-all hover:shadow-md group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${type === 'mobile' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>
                                            {getDeviceIcon(type)}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 mb-0.5">
                                                <p className="font-semibold text-gray-900 text-xs truncate">
                                                    {os} &bull; {browser}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                                <div className="flex items-center gap-0.5 shrink-0">
                                                    <MapPin size={10} className="text-gray-400" />
                                                    <span className="truncate max-w-[80px]">
                                                        {session.city && session.countryCode
                                                            ? `${session.city.replace("Arrondissement de ", "").replace("Arrondissement ", "")}, ${session.countryCode}`
                                                            : session.countryCode || "Inconnu"
                                                        }
                                                    </span>
                                                </div>
                                                <div className="bg-gray-200 w-px h-2"></div>
                                                <div className="flex items-center gap-0.5 shrink-0">
                                                    <Clock size={10} className="text-gray-400" />
                                                    <span>{formatDate(session.lastActivityAt)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleRevokeSession(session.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                        title={t('sessions.logout')}
                                    >
                                        <LogOut size={16} />
                                    </button>
                                </div>
                            )
                        })}
                        {sessions.length === 0 && (
                            <div className="text-center py-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200 flex-1 flex flex-col items-center justify-center">
                                <Smartphone className="mx-auto h-8 w-8 text-gray-300 mb-2" />
                                <p className="text-gray-400 text-xs">{t('sessions.noDevices')}</p>
                            </div>
                        )}
                    </div>

                    {sessions.length > 2 && (
                        <button
                            onClick={() => setIsDevicesExpanded(!isDevicesExpanded)}
                            className="w-full mt-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                            {isDevicesExpanded ? (
                                <>
                                    <span>{t('sessions.seeLess')}</span>
                                    <ChevronUp size={14} />
                                </>
                            ) : (
                                <>
                                    <span>{t('sessions.seeMore', { count: sessions.length - 2 })}</span>
                                    <ChevronDown size={14} />
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {/* SECURITY QUESTIONS SECTION */}
            <div className={`bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-sm flex flex-col ${securityStatus?.hasSecurityQuestions ? '' : 'border-l-4 border-l-yellow-400'}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-4">
                    <div className="flex gap-4">
                        <div className={`p-3 rounded-full shrink-0 h-10 w-10 flex items-center justify-center ${securityStatus?.hasSecurityQuestions ? "bg-blue-50 text-blue-600" : "bg-yellow-50 text-yellow-600"}`}>
                            <HelpCircle size={20} />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 leading-tight">{t('security.title')}</h3>
                            <p className="text-gray-500 text-xs mt-1 max-w-xs">
                                {t('security.subtitle')}
                            </p>
                        </div>
                    </div>
                    <div className="self-end sm:self-start shrink-0">
                        <button
                            onClick={handleSecurityExpand}
                            // Disabled if configured (hide Modify option assumption) or handle logic differently
                            // User request: "retire option modifye a depil fin setup"
                            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm ${isSecurityExpanded
                                ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                : securityStatus?.hasSecurityQuestions
                                    ? "hidden" // HIDE if configured
                                    : "text-white bg-yellow-500 hover:bg-yellow-600 shadow-yellow-200"
                                }`}
                        >
                            {isSecurityExpanded ? (
                                <><span>{t('security.close')}</span><ChevronUp size={14} /></>
                            ) : (
                                <><span>{t('security.configure')}</span><ChevronDown size={14} /></>
                            )}
                        </button>
                    </div>
                </div>

                {/* Status Indicator */}
                {!isSecurityExpanded && (
                    <div className="mt-1">
                        {securityStatus?.hasSecurityQuestions ? (
                            <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50/50 p-2 rounded-lg border border-green-100">
                                <ShieldCheck size={14} />
                                <span className="font-medium">{t('security.configured')}</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50/50 p-2 rounded-lg border border-yellow-100">
                                <HelpCircle size={14} />
                                <span className="font-medium">{t('security.notConfigured')}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Configuration Form */}
                <AnimatePresence>
                    {isSecurityExpanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="mt-4 space-y-4 pt-4 border-t border-gray-100">
                                {[0, 1, 2].map((index) => (
                                    <div key={index} className="space-y-2 bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-gray-100 text-gray-600 text-[10px]">{index + 1}</span>
                                            {t('security.questionLabel', { num: index + 1 })}
                                        </div>
                                        <select
                                            value={securityForm[index].questionId}
                                            onChange={(e) => handleQuestionChange(index, e.target.value)}
                                            className="w-full p-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                                        >
                                            <option value="">{t('security.selectQuestion')}</option>
                                            {availableQuestions
                                                .filter(q => !securityForm.some((f, i) => i !== index && f.questionId === q.id)) // Filter out selected ones except current
                                                .map(q => (
                                                    <option key={q.id} value={q.id}>
                                                        {t(q.questionText)}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                        <div className="relative">
                                            <input
                                                type={showAnswers[index] ? "text" : "password"}
                                                placeholder={t('security.answerPlaceholder')}
                                                value={securityForm[index].answer}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                className="w-full p-2 pr-10 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-500 focus:bg-blue-50/10 transition-colors"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => toggleAnswerVisibility(index)}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                {showAnswers[index] ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <div className="flex justify-end pt-2">
                                    <button
                                        onClick={handleSaveSecurityQuestions}
                                        disabled={isSavingSecurity}
                                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 shadow-sm shadow-blue-200 disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {isSavingSecurity && <Loader2 className="animate-spin" size={16} />}
                                        {t('security.save')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Custom Modal for 2FA Deactivation */}
            <AnimatePresence>
                {showDeactivateModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 border border-gray-100"
                        >
                            <div className="flex flex-col items-center text-center mb-6">
                                <div className="p-3 bg-red-50 text-red-600 rounded-full mb-4">
                                    <ShieldCheck size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">{t('2fa.confirmDisable')}</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    {t('2fa.confirmDisableDesc')}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                                        {t('2fa.enterCodeConfirm')}
                                    </label>
                                    <div className="flex flex-col items-center">
                                        <div className="relative">
                                            {isEnabling && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-10">
                                                    <Loader2 className="animate-spin text-red-600" size={24} />
                                                </div>
                                            )}
                                            <InputOTP
                                                maxLength={6}
                                                disabled={isEnabling}
                                                value={deactivateToken}
                                                onChange={(val) => {
                                                    setDeactivateError(""); // Clear error on change
                                                    setDeactivateToken(val);
                                                    if (val.length === 6) {
                                                        confirmDisable2FA(val);
                                                    }
                                                }}
                                                className={deactivateError ? "ring-2 ring-red-500 rounded-lg transition-all" : ""}
                                            >
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={0} mask className={deactivateError ? "border-red-300" : ""} />
                                                    <InputOTPSlot index={1} mask className={deactivateError ? "border-red-300" : ""} />
                                                    <InputOTPSlot index={2} mask className={deactivateError ? "border-red-300" : ""} />
                                                </InputOTPGroup>
                                                <InputOTPGroup>
                                                    <InputOTPSlot index={3} mask className={deactivateError ? "border-red-300" : ""} />
                                                    <InputOTPSlot index={4} mask className={deactivateError ? "border-red-300" : ""} />
                                                    <InputOTPSlot index={5} mask className={deactivateError ? "border-red-300" : ""} />
                                                </InputOTPGroup>
                                            </InputOTP>
                                        </div>
                                        {/* Error Message */}
                                        <AnimatePresence>
                                            {deactivateError && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10, height: 0 }}
                                                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                                                    exit={{ opacity: 0, y: -10, height: 0 }}
                                                    className="mt-2 text-center"
                                                >
                                                    <p className="text-xs font-semibold text-red-600 flex items-center gap-1">
                                                        <Shield size={12} />
                                                        {deactivateError}
                                                    </p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={() => {
                                            setShowDeactivateModal(false);
                                            setDeactivateToken("");
                                        }}
                                        className="w-full py-2.5 text-gray-600 bg-gray-100 hover:bg-gray-200 font-medium rounded-xl text-sm transition-colors"
                                    >
                                        {t('2fa.cancel')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
